import { useRef, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import classNames from "classnames";

import { useCurrentRoute } from "../Routing/useCurrentRoute";
import { useAppContainer } from "../Main/AppContainerContext";

const NavigationIndicator = () => {
  const appContainer = useAppContainer();
  const currentRoute = useCurrentRoute();
  const currentLocation = useSelector(({ location }) => location.currentLocation);

  const routerPath = currentRoute.location.pathname;
  const storedPath = currentLocation?.pathname;
  const isNavigating = storedPath && routerPath !== storedPath;

  const navigationIndicator = document.getElementById("navigation-indicator");

  const displayUpdateTimerRef = useRef(null);

  const updateDisplay = useCallback(
    display => {
      if (displayUpdateTimerRef.current) {
        clearTimeout(displayUpdateTimerRef.current);
      }

      // Make the indicator appear quickly, but disappear slowly to avoid flickering.
      // Still use a delay when showing it to avoid showing it for very quick navigations.
      const delay = display ? 50 : 500;

      displayUpdateTimerRef.current = setTimeout(() => {
        const action = display ? "add" : "remove";
        navigationIndicator.classList[action]("shown");
      }, delay);
    },
    [navigationIndicator]
  );

  // Cancel any pending display update on unmount
  useEffect(() => {
    return () => {
      if (displayUpdateTimerRef.current) {
        clearTimeout(displayUpdateTimerRef.current);
      }
    };
  }, []);

  if (navigationIndicator) {
    const { classList } = navigationIndicator;
    const wasNavigating = classList?.contains("shown");

    if (isNavigating && !wasNavigating) {
      updateDisplay(true);
    } else if (!isNavigating && wasNavigating) {
      updateDisplay(false);
    }
  }

  const className = classNames("navigation-indicator", "progress", {
    shown: isNavigating
  });

  return createPortal(
    <div id="navigation-indicator" className={className}>
      <div className="progress-bar progress-bar-striped progress-bar-animated" />
    </div>,
    appContainer
  );
};

export default NavigationIndicator;
